import React, { forwardRef, Ref, KeyboardEvent } from 'react'
import styled from 'styled-components'
import { color } from 'styled-system'
import { textStyle } from '@zuto/utilities'
import { TextStyle } from '@zuto/types'
import { Input, InputProps } from '@zuto/atoms'

export interface CurrencyInputProps extends Omit<InputProps, 'name' | 'icon'> {
  /** Used to link the label and the field */
  name?: string
  testId?: string
}

const CurrencySymbol = styled.span<React.HTMLAttributes<HTMLSpanElement> & { textStyle: TextStyle, $color: string }>`
  ${textStyle}
  ${(props) => color({ ...props, color: props.$color })}
  height: 50px;
  line-height: 50px;
  position: absolute;
  padding-left: 16px;
`

const StyledInput = styled(Input)`
  input {
    font-family: Helvetica, sans-serif;
  }
`

const CurrencyInput = forwardRef((
  {
    onChange,
    onBlur,
    onKeyPress,
    testId,
    ...props
  }: CurrencyInputProps,
  ref: Ref<HTMLInputElement>
) : JSX.Element => {
  const stopNegativeValues = (input: HTMLInputElement) => {
    const valueAsInt = parseInt(input.value, 10)
    return Math.abs(valueAsInt) >= 0 ? Math.abs(valueAsInt).toString() : ''
  }

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.currentTarget.value = stopNegativeValues(e.currentTarget)

    if (onChange) {
      onChange(e)
    }
  }

  const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.value = e.currentTarget.value.replace(/[^0-9]/g, '')

    if (onBlur) {
      onBlur(e)
    }
  }

  const handleEnterPress = (e: KeyboardEvent) => {
    if (onKeyPress) {
      if (e.code === 'Enter') {
        onKeyPress(e)
      }
    }
  }

  return (
    <StyledInput
      {...props}
      onChange={onChangeHandler}
      onBlur={onBlurHandler}
      onKeyPress={handleEnterPress}
      name="currency-input"
      icon={(
        <CurrencySymbol
          id="currency-icon"
          textStyle="body.regular.medium"
          $color="neutrals.4"
        >
          £
        </CurrencySymbol>
)}
      type="number"
      step="1"
      min="1"
      data-testid={testId || 'currency-input'}
      ref={ref}
      pattern="[0-9]*"
    />
  )
})

export default CurrencyInput
